import { UnreachableError } from 'base/helpers';
import Color from 'colorjs.io';
import tinycolor from 'tinycolor2';
import {
  extractPaletteColor,
  generateNeutralShades,
  generateShades,
} from './color';
import { CardStyle, ConceptState, UiStyle } from './model';
import {
  AlternateTarget,
  ColorVariable,
  CompiledChromaticColor,
  CompiledConcept,
  CompiledNeutralColor,
  CompiledScheme,
  ConceptVariable,
  NEUTRAL_DARKEST,
  OPACITY_MODIFIERS,
  PaletteName,
  ShadeDescriptor,
  ShadeKey,
} from './types';

export const APCATextContrastThreshold = 54;
export const APCAElementContrastThreshold = 15;

export const getSchemeForSchemelessComponent = ({
  darkAlternate,
}: {
  darkAlternate: boolean;
}): CompiledScheme => {
  return {
    palette: 'neutral',
    shade: -1,
    background: 'transparent',
    foreground: 'transparent',
    text: 'neutral-white',
    accent: 'neutral-white',
    border: 'transparent',
    alternate: new Set([
      ...(darkAlternate ? [] : (['primary-button'] as const)),
      'form-input',
      'tagline',
      'logo',
    ]),
    isDark: false,
  };
};

export function compileChromatics(
  concept: ConceptState,
): ReadonlyArray<CompiledChromaticColor> {
  return concept.chromatics.map(({ hex, name, locked }, index) => {
    const shades = generateShades(hex.toUpperCase());

    return {
      palette: 'chromatic',
      name: name,
      hex: hex.toUpperCase(),
      shades,
      locked,
      chromatic: index + 1,
    };
  });
}

/** When we tint the neutral palette this is the opacity of the tint layer. */
export const NEUTRAL_TINT_OPACITY = 0.05;

export function compileNeutral(concept: ConceptState): CompiledNeutralColor {
  const tint =
    concept.neutral.tintIndex != null
      ? concept.chromatics[concept.neutral.tintIndex]?.hex
      : undefined;

  const neutralHex = (
    tint != null
      ? tinycolor.mix('black', tint, NEUTRAL_TINT_OPACITY * 100).toHexString()
      : '#000000'
  ).toUpperCase();

  const neutralShades = generateNeutralShades(neutralHex);
  return {
    palette: 'neutral',
    hex: neutralHex,
    shades: neutralShades,
    locked: concept.neutral.locked,
  };
}

export function generateColorVariables(concept: {
  state: ConceptState;
  neutral: CompiledNeutralColor;
  chromatics: ReadonlyArray<CompiledChromaticColor>;
}): Map<ColorVariable, string> {
  const result = new Map<ColorVariable, string>();

  for (let i = 0; i < concept.neutral.shades.length; i++) {
    result.set(
      assertColorVariable(`neutral-shade-${i}`),
      concept.neutral.shades[i],
    );
  }

  const neutralWhite = '#FFFFFF';
  const neutralDarkest = concept.neutral.shades[7];

  result.set('neutral-white', neutralWhite);
  result.set('transparent', 'transparent');

  for (const n of OPACITY_MODIFIERS) {
    result.set(
      `${NEUTRAL_DARKEST}-${n}`,
      `color-mix(in srgb, ${neutralDarkest}, transparent ${100 - n}%)`,
    );
    result.set(
      `neutral-white-${n}`,
      `color-mix(in srgb, ${neutralWhite}, transparent ${100 - n}%)`,
    );
  }

  for (let i = 0; i < concept.chromatics.length; i++) {
    const shades = concept.chromatics[i].shades;
    const paletteName = `chromatic${i + 1}`;

    for (let i = 0; i < shades.length; i++) {
      result.set(
        assertColorVariable(`${paletteName}-shade-${i + 1}`),
        shades[i],
      );
    }
  }

  return result;
}
export function assertColorVariable(v: string): ColorVariable {
  if (v === 'none' || v === 'neutral-white' || v === 'transparent') {
    return v as ColorVariable;
  }

  const neutralWhiteMatch = v.match(/^neutral-white-(\d+)$/);
  if (
    neutralWhiteMatch &&
    OPACITY_MODIFIERS.map((n) => `${n}`).includes(neutralWhiteMatch[1])
  ) {
    return v as ColorVariable;
  }

  const neutralDarkestMatch = v.match(/^neutral-shade-7-(\d+)$/);
  if (
    neutralDarkestMatch &&
    OPACITY_MODIFIERS.map((n) => `${n}`).includes(neutralDarkestMatch[1])
  ) {
    return v as ColorVariable;
  }

  return assertShadeKey(v);
}

export function assertShadeKey(name: string): ShadeKey {
  const isNeutralShade = !!/^neutral-shade-[0-7]$/.exec(name);
  const isChromaticShade = !!/^chromatic[1-5]-shade-[1-7]$/.exec(name);
  if (!isNeutralShade && !isChromaticShade) {
    throw new Error(`${name} is not a valid scheme name`);
  }

  return name as ShadeKey;
}

export function assertPaletteName(name: string): PaletteName {
  if (!/chromatic[1-5]/.exec(name) && name !== 'neutral') {
    throw new Error(`${name} is not a valid palette name`);
  }
  return name as PaletteName;
}

export function compileConcept(concept: ConceptState): CompiledConcept {
  const neutral = compileNeutral(concept);
  const chromatics = compileChromatics(concept);
  const colorVariables = generateColorVariables({
    state: concept,
    neutral,
    chromatics,
  });
  const cardStyle = concept.cardStyle ?? CardStyle.Default;

  return {
    state: concept,
    chromatics,
    neutral,
    schemes: generateSchemes({
      neutral,
      chromatics,
      colorVariables,
      cardStyle,
    }),
    colorVariables,
  };
}

export function getShadeKey(scheme: ShadeDescriptor): ShadeKey {
  return assertShadeKey(
    scheme.palette === 'neutral'
      ? `neutral-shade-${scheme.shade}`
      : `chromatic${scheme.chromatic}-shade-${scheme.shade}`,
  );
}

export function generateSchemes(concept: {
  colorVariables: ReadonlyMap<ColorVariable, string>;
  chromatics: ReadonlyArray<CompiledChromaticColor>;
  neutral: CompiledNeutralColor;
  cardStyle: CardStyle;
}): CompiledScheme[] {
  const schemes: CompiledScheme[] = [];

  for (let shade = 0; shade < 8; shade++) {
    schemes.push(generateScheme({ palette: 'neutral', shade }, concept));
  }

  for (
    let chromatic = 1;
    chromatic < concept.chromatics.length + 1;
    chromatic++
  )
    for (let shade = 1; shade < 8; shade++) {
      schemes.push(
        generateScheme({ palette: 'chromatic', shade, chromatic }, concept),
      );
    }

  return schemes;
}

export function generateScheme(
  scheme: ShadeDescriptor,
  concept: {
    colorVariables: ReadonlyMap<ColorVariable, string>;
    chromatics: ReadonlyArray<CompiledChromaticColor>;
    neutral: CompiledNeutralColor;
    cardStyle?: CardStyle;
  },
): CompiledScheme {
  const dereference = (color: ColorVariable) =>
    extractPaletteColor(concept, color);

  const background = assertColorVariable(
    `${
      scheme.palette === 'neutral' ? 'neutral' : `chromatic${scheme.chromatic}`
    }-shade-${scheme.shade}`,
  );

  let foreground: ColorVariable;

  switch (concept.cardStyle) {
    case undefined:
    case CardStyle.Default:
    case CardStyle.Flat:
      foreground = computeForegroundColor(background);
      break;
    case CardStyle.Outlined:
    case CardStyle.Edgy:
      foreground = background;
      break;
    default:
      throw new UnreachableError(concept.cardStyle);
  }

  const primary = 'chromatic1-shade-4';
  const primaryHex = dereference(primary);
  const white = 'neutral-white';
  const whiteHex = dereference(white);

  const backgroundHex = dereference(background);
  const foregroundHex = dereference(foreground);
  const averageBackgroundColor = Color.mix(backgroundHex, foregroundHex);

  const whiteOnAverageBackground = Math.abs(
    Color.contrastAPCA(averageBackgroundColor, whiteHex),
  );

  // APAC contrast is different depending on which around the colours are.
  const primaryContrastOnNeutralZero = Math.abs(
    Color.contrastAPCA(whiteHex, primaryHex),
  );
  const primaryContrastOnAverageBackground = Math.abs(
    Color.contrastAPCA(averageBackgroundColor, primaryHex),
  );

  // Default to white text unless the contrast against the average background
  // colour falls below 54, then use Neutral Darkest text.
  const text: ColorVariable =
    whiteOnAverageBackground < APCATextContrastThreshold
      ? NEUTRAL_DARKEST
      : white;

  const accent =
    primaryContrastOnAverageBackground < APCATextContrastThreshold
      ? text
      : primary;

  const alternate = new Set<AlternateTarget>();

  // Use the scheme accent colour for taglines. If contrast drops below 54,
  // inherit the text colour from the section.
  if (primaryContrastOnNeutralZero < APCATextContrastThreshold) {
    alternate.add('tagline');
  }

  // Use default style unless the button stroke contrast is less than 15. If so,
  // switch to the alternate button style
  if (primaryContrastOnAverageBackground < APCAElementContrastThreshold) {
    alternate.add('primary-button');
  }

  // Form fields
  // Switch to alternate whenever we're using white text colour.
  if (text === 'neutral-white') {
    alternate.add('form-input');
  }

  // Use the alternate (dark mode) logo when the background is considered dark.
  if (whiteOnAverageBackground >= APCATextContrastThreshold) {
    alternate.add('logo');
  }

  let border: ColorVariable;

  switch (concept.cardStyle) {
    case undefined:
    case CardStyle.Default:
    case CardStyle.Outlined:
    case CardStyle.Flat:
      border =
        text === 'neutral-white' ? 'neutral-white-20' : `${NEUTRAL_DARKEST}-15`;
      break;
    case CardStyle.Edgy:
      border = text;
      break;
    default:
      throw new UnreachableError(concept.cardStyle);
  }

  const isDark = whiteOnAverageBackground < APCATextContrastThreshold;

  return {
    ...scheme,
    background,
    foreground,
    text,
    accent,
    border,
    alternate,
    isDark,
  };
}

export function computeForegroundColor(bgColor: ColorVariable): ColorVariable {
  if (bgColor === 'neutral-shade-0') return 'neutral-shade-1';
  if (bgColor === 'neutral-shade-1') return 'neutral-shade-0';

  const shade = Number(/shade-(\d+)/.exec(bgColor)![1]);
  const color = bgColor.split('-')[0];
  const result = [
    `${color}-shade-2`,
    `${color}-shade-1`,
    `${color}-shade-4`,
    `${color}-shade-5`,
    `${color}-shade-4`,
    `${color}-shade-7`,
    `${color}-shade-6`,
  ];

  return assertColorVariable(result[shade - 1]);
}

/**
 * Concept variables are needed in the SGB to buttons, form fields, and other
 * These will be encoded into the elements during export to webflow/figma but
 * for now this is needed to generate the correct styles.
 */
export function generateConceptVariables(concept: {
  colorVariables: ReadonlyMap<ColorVariable, string>;
  neutral: CompiledNeutralColor;
  chromatics: ReadonlyArray<CompiledChromaticColor>;
  cardStyle?: CardStyle;
}): Record<ConceptVariable, ColorVariable> {
  const dereference = (color: ColorVariable) =>
    extractPaletteColor(concept, color);

  const primaryShade = {
    palette: 'chromatic',
    chromatic: 1,
    shade: 4,
  } satisfies ShadeDescriptor;

  const primary = getShadeKey(primaryShade);
  const primaryHex = dereference(primary);
  const white = 'neutral-white';
  const whiteHex = dereference(white);
  const neutralDarkestHex = dereference(NEUTRAL_DARKEST);

  const { text } = generateScheme(primaryShade, concept);

  const whiteContrastOnPrimary = Math.abs(
    Color.contrastAPCA(primaryHex, whiteHex),
  );
  const primaryContrastOnWhite = Math.abs(
    Color.contrastAPCA(whiteHex, primaryHex),
  );
  const primaryContrastOnNeutralDarkest = Math.abs(
    Color.contrastAPCA(neutralDarkestHex, primaryHex),
  );

  const isDarkPrimaryColor =
    primaryContrastOnWhite > primaryContrastOnNeutralDarkest;

  return {
    'button-text': text,
    'light-button-text':
      isDarkPrimaryColor || whiteContrastOnPrimary < APCATextContrastThreshold
        ? NEUTRAL_DARKEST
        : white,
    'light-button-background': isDarkPrimaryColor ? white : primary,
    'alternate-button-text': isDarkPrimaryColor ? NEUTRAL_DARKEST : white,
    'alternate-button-background': isDarkPrimaryColor ? white : NEUTRAL_DARKEST,
  };
}

export const getPrimaryButtonStyling = ({
  concept,
  uiStyle,
}: {
  concept: {
    colorVariables: ReadonlyMap<ColorVariable, string>;
    neutral: CompiledNeutralColor;
    chromatics: ReadonlyArray<CompiledChromaticColor>;
  };
  uiStyle: UiStyle;
}): { darkText: boolean; darkAlternate: boolean } => {
  if (uiStyle === UiStyle.Brick) {
    return { darkText: false, darkAlternate: false };
  }

  const dereference = (color: ColorVariable) =>
    extractPaletteColor(concept, color);

  const primaryShade = {
    palette: 'chromatic',
    chromatic: 1,
    shade: 4,
  } satisfies ShadeDescriptor;

  const primary = getShadeKey(primaryShade);
  const primaryHex = dereference(primary);
  const white = 'neutral-white';
  const whiteHex = dereference(white);
  const neutralDarkestHex = dereference(NEUTRAL_DARKEST);

  const { text } = generateScheme(primaryShade, concept);
  const darkText = text === NEUTRAL_DARKEST;

  const primaryContrastOnWhite = Math.abs(
    Color.contrastAPCA(whiteHex, primaryHex),
  );
  const primaryContrastOnNeutralDarkest = Math.abs(
    Color.contrastAPCA(neutralDarkestHex, primaryHex),
  );

  return {
    darkText,
    darkAlternate: primaryContrastOnWhite < primaryContrastOnNeutralDarkest,
  };
};

const ShadePostfixes = [
  ' White', // Only used for neutral
  ' Lightest',
  ' Lighter',
  ' Light',
  '', // Base
  ' Dark',
  ' Darker',
  ' Darkest',
];

export const getNeutralShadeName = (shade: number) =>
  `Neutral${ShadePostfixes[shade]}`;

export const getChromaticShadeName = (
  chromatic: CompiledChromaticColor,
  shade: number,
) => `${chromatic.name}${ShadePostfixes[shade]}`;

export const parseNeutralShadeName = (name: string): number => {
  if (name === 'White') {
    return 0;
  }

  const m = name.match(/^Neutral( \w+)$/);
  if (!m) throw new Error(`Invalid neutral shade name: ${name}`);

  const shade = ShadePostfixes.indexOf(m[1]);
  if (shade === -1) throw new Error(`Invalid neutral shade name: ${name}`);

  return shade;
};
